import React, { useState, FormEvent, useRef, useEffect, useCallback } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { completeCreditPurchase } from '../services/credits';
import { useTracking } from '../hooks/useTracking';
import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
import { API_CONFIG } from '../utils/constants';

interface CreditCheckoutFormProps {
  clientSecret: string;
  customerId: string;
  packId: 'basic' | 'pro' | 'premium';
  userId: string;
  packPrice?: number; // DEPRECATED: No longer used - value is hardcoded to $20
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

// Inner form component that uses Stripe hooks
function CreditCheckoutForm({ 
  clientSecret, 
  customerId,
  packId,
  userId,
  packPrice,
  onComplete, 
  onError 
}: CreditCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { track } = useTracking();
  const posthog = useDeduplicatedPostHog(); // Use deduplicated wrapper
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const propertiesSetRef = useRef(false); // Track if properties were set for this transaction
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const paymentElementMountTimeRef = useRef<number>(Date.now());

  // Track when PaymentElement successfully mounts
  const handlePaymentElementReady = useCallback(() => {
    const mountDuration = Date.now() - paymentElementMountTimeRef.current;
    setPaymentElementReady(true);
    
    track('payment_element_ready', {
      component: 'CreditEmbeddedCheckout',
      pack_id: packId,
      mount_duration_ms: mountDuration,
    });
    
    console.log(`✅ [CreditEmbeddedCheckout] PaymentElement ready in ${mountDuration}ms`);
  }, [track, packId]);

  // Track PaymentElement errors and completion
  const handlePaymentElementChange = useCallback((event: any) => {
    if (event.error) {
      console.error('❌ [CreditEmbeddedCheckout] PaymentElement error:', event.error);
      
      track('payment_element_error', {
        component: 'CreditEmbeddedCheckout',
        error_type: event.error.type,
        error_code: event.error.code,
        error_message: event.error.message,
        pack_id: packId,
      });
    }
    
    if (event.complete) {
      track('payment_element_complete', {
        component: 'CreditEmbeddedCheckout',
        pack_id: packId,
      });
    }
  }, [track, packId]);

  // Timeout detection: if PaymentElement doesn't become ready within 10 seconds, show error
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!paymentElementReady) {
        const waitTime = Date.now() - paymentElementMountTimeRef.current;
        
        console.error(`❌ [CreditEmbeddedCheckout] PaymentElement timeout after ${waitTime}ms`);
        
        track('payment_element_timeout', {
          component: 'CreditEmbeddedCheckout',
          pack_id: packId,
          wait_time_ms: waitTime,
          stripe_loaded: !!stripe,
          elements_loaded: !!elements,
        });
        
        setErrorMessage(
          'Payment form is taking longer than expected to load. ' +
          'Please refresh the page and try again. If the problem persists, contact support.'
        );
        
        if (onError) {
          onError(new Error('PaymentElement mount timeout'));
        }
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [paymentElementReady, stripe, elements, track, packId, onError]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Submit payment form to Stripe
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment form validation failed');
        setIsProcessing(false);
        return;
      }

      // Confirm SetupIntent to collect payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required', // Only redirect if required (e.g., 3D Secure)
      });

      if (confirmError) {
        setErrorMessage(confirmError.message || 'Payment method collection failed');
        if (onError) {
          onError(new Error(confirmError.message || 'Payment method collection failed'));
        }
        setIsProcessing(false);
        return;
      }

      if (!setupIntent || !setupIntent.payment_method) {
        throw new Error('Payment method not collected');
      }

      // Payment method collected, now complete credit purchase
      const result = await completeCreditPurchase(
        customerId,
        setupIntent.payment_method as string,
        packId,
        userId
      );

      if (result.success) {
        // Track purchase event (credit pack purchase)
        // Hardcoded conversion value - all credit packs cost $20
        track('purchase', {
          transaction_id: `credit_${Date.now()}_${packId}`,
          plan_id: packId,
          plan_name: packId,
          currency: 'USD',
          value: 20, // Hardcoded: All credit packs = $20
          product_type: 'credit_pack', // Distinguish from subscriptions
          user_id: userId,
          timestamp: new Date().toISOString(),
        });

        // Update user properties with payment info (deduplicated via wrapper)
        if (posthog && !propertiesSetRef.current) {
          posthog.setPersonProperties({
            has_payment_method: true,
            last_credit_pack_purchased: packId,
            last_purchase_date: new Date().toISOString(),
            payment_method_type: 'card',
          });
          propertiesSetRef.current = true; // Mark as set to prevent duplicates in this transaction
        }
        
        // Purchase completed successfully
        if (onComplete) {
          onComplete();
        }
      } else {
        throw new Error('Credit purchase failed');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unexpected error occurred');
      setErrorMessage(error.message);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      <div className="bg-white/5 border border-white/20 rounded-lg p-3 sm:p-4 relative">
        {!paymentElementReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded-lg">
            <div className="text-center space-y-2">
              <div className="animate-pulse text-white/70">Loading payment form...</div>
              <div className="text-xs text-white/50">Please wait</div>
            </div>
          </div>
        )}
        
        <PaymentElement 
          onReady={handlePaymentElementReady}
          onChange={handlePaymentElementChange}
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {errorMessage && (
        <div className="p-3 sm:p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {errorMessage}
        </div>
      )}

      <div className="sticky bottom-0 bg-bg pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0.5rem))] sm:pb-[calc(1rem+env(safe-area-inset-bottom,1rem))]">
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-lg font-semibold hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          data-testid="purchase-credits-button"
        >
          {isProcessing ? 'Processing...' : 'Purchase Credits'}
        </button>
      </div>
    </form>
  );
}

// Main component that wraps with Elements provider
interface CreditEmbeddedCheckoutProps {
  clientSecret: string;
  customerId: string;
  packId: 'basic' | 'pro' | 'premium';
  userId: string;
  packPrice?: number; // DEPRECATED: No longer used - value is hardcoded to $20
  publishableKey?: string; // Optional: if provided, use this key instead of fetching
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export default function CreditEmbeddedCheckout({ 
  clientSecret,
  customerId,
  packId,
  userId,
  packPrice,
  publishableKey,
  onComplete,
  onError 
}: CreditEmbeddedCheckoutProps) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        setLoading(true);
        setError(null);

        let key = publishableKey;

        // If no key provided, fetch from backend config endpoint
        if (!key) {
          try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/config`);
            const data = await response.json();
            
            if (data.success && data.publishable_key) {
              key = data.publishable_key;
            } else {
              throw new Error('Invalid response from config endpoint');
            }
          } catch (configError) {
            console.error('[CreditEmbeddedCheckout] Failed to fetch config:', configError);
            throw new Error('Failed to get Stripe configuration from backend');
          }
        }

        if (!key) {
          throw new Error('Stripe publishable key is required');
        }

        // Initialize Stripe with the key
        const stripe = loadStripe(key);
        setStripePromise(stripe);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Stripe';
        console.error('❌ [CreditEmbeddedCheckout] Stripe initialization error:', err);
        setError(errorMessage);
        if (onError) {
          onError(new Error(errorMessage));
        }
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [publishableKey, onError]);

  if (loading) {
    return (
      <div className="p-4 bg-white/5 border border-white/20 rounded-lg text-white/70 text-center">
        Initializing payment form...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
        Stripe is not configured. Please check your backend connection.
      </div>
    );
  }

  return (
    <div className="w-full" id="credit-embedded-checkout">
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#ffffff',
              colorBackground: '#000000',
              colorText: '#ffffff',
              colorDanger: '#ef4444',
              fontFamily: 'system-ui, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
          },
        }}
      >
        <CreditCheckoutForm 
          clientSecret={clientSecret}
          customerId={customerId}
          packId={packId}
          userId={userId}
          packPrice={packPrice}
          onComplete={onComplete} 
          onError={onError} 
        />
      </Elements>
    </div>
  );
}

